import { Badge, Box, Button, Group, Tabs, Text } from '@mantine/core';
import { IconBuildingSkyscraper, IconPlus, IconStairs } from '@tabler/icons-react';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import Tabla from '../../../../globales/componentes/tabla/tabla';
import type { IEquipoServicioAscensor } from '../../../../modelos/CotizadorServicios/entidades/IEquipoServicioAscensor';
import type { IEquipoServicioEscalera } from '../../../../modelos/CotizadorServicios/entidades/IEquipoServicioEscalera';
import { columnasAscensores, columnasEscaleras } from '../constantesTablaEquiposServicios';
import type { ITablaEquiposMeta } from '../../interfaces';

interface TablaEquiposProps<T extends { Id: string }> {
    datos: T[];
    columnas: ColumnDef<T>[];
    meta: ITablaEquiposMeta<T>;
    textoAgregar: string;
    alAgregar: () => void;
}

const TablaEquipos = <T extends { Id: string }>({ datos, columnas, meta, textoAgregar, alAgregar }: TablaEquiposProps<T>) => {
    const tabla = useReactTable({
        data: datos,
        columns: columnas,
        getCoreRowModel: getCoreRowModel(),
        getRowId: fila => fila.Id,
        meta
    });

    return (
        <Box>
            {!meta.soloLectura && (
                <Group justify="flex-end" mb="sm">
                    <Button color="primary" variant="light" size="xs" radius="lg" leftSection={<IconPlus size={12} />} onClick={alAgregar} disabled={meta.filaEnEdicion !== null}>
                        <Text fw={500} size="xs">{textoAgregar}</Text>
                    </Button>
                </Group>
            )}
            <Tabla tabla={tabla} modoEdicion={meta.filaEnEdicion !== null} filaEnEdicion={meta.filaEnEdicion} />
            {datos.length === 0 && <Text size="xs" c="dimmed" ta="center" mt="sm">Aún no hay equipos agregados.</Text>}
        </Box>
    );
};

interface PasoEquiposVistaProps {
    ascensores: IEquipoServicioAscensor[];
    escaleras: IEquipoServicioEscalera[];
    metaAscensores: ITablaEquiposMeta<IEquipoServicioAscensor>;
    metaEscaleras: ITablaEquiposMeta<IEquipoServicioEscalera>;
    alAgregarAscensor: () => void;
    alAgregarEscalera: () => void;
    /** Al cambiar de pestaña se descarta la fila en edición. */
    alCambiarPestana: () => void;
}

/** Paso 2: ascensores y escaleras/rampas/andenes a cotizar. */
export const PasoEquiposVista: React.FC<PasoEquiposVistaProps> = ({
    ascensores,
    escaleras,
    metaAscensores,
    metaEscaleras,
    alAgregarAscensor,
    alAgregarEscalera,
    alCambiarPestana
}) => (
    <Tabs defaultValue="ascensores" onChange={alCambiarPestana}>
        <Tabs.List mb="md">
            <Tabs.Tab value="ascensores" leftSection={<IconBuildingSkyscraper size={14} />} rightSection={<Badge size="xs" variant="light" circle>{ascensores.length}</Badge>}>
                Ascensores
            </Tabs.Tab>
            <Tabs.Tab value="escaleras" leftSection={<IconStairs size={14} />} rightSection={<Badge size="xs" variant="light" circle>{escaleras.length}</Badge>}>
                Escaleras, Rampas y Andenes
            </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="ascensores">
            <TablaEquipos datos={ascensores} columnas={columnasAscensores} meta={metaAscensores} textoAgregar="Agregar Ascensor" alAgregar={alAgregarAscensor} />
        </Tabs.Panel>
        <Tabs.Panel value="escaleras">
            <TablaEquipos datos={escaleras} columnas={columnasEscaleras} meta={metaEscaleras} textoAgregar="Agregar Equipo" alAgregar={alAgregarEscalera} />
        </Tabs.Panel>
    </Tabs>
);
